import React, { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useTheme } from "../contexts/ThemeContext"
import { useErrorFeedback } from "../hooks/useErrorFeedback"
import { ErrorFeedback } from "../components/shared/ErrorFeedback"
import { createUserProfile, createUserWithEmailAndPassword } from "../utils/firebase"
import { useAuthStore } from "../store/useAuthStore"
import { useTranslation } from "react-i18next"

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { label: string }>(
  ({ label, ...props }, ref) => {
    const { colors, isDarkMode } = useTheme()
    return (
      <div className="space-y-1">
        <label htmlFor={props.id} className="block text-sm font-medium" style={{ color: colors.textSecondary }}>
          {label}
        </label>
        <input
          ref={ref}
          className="w-full px-4 py-2 rounded-lg transition-colors duration-200 border focus:border-primary focus:outline-none"
          style={{
            backgroundColor: isDarkMode ? colors.surfaceAlt : colors.background,
            color: colors.text,
            borderColor: colors.border,
          }}
          {...props}
        />
      </div>
    )
  },
)

const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...props }) => {
  const { colors } = useTheme()
  return (
    <button
      className="w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center min-h-[48px] hover:opacity-90"
      style={{
        backgroundColor: colors.primary,
        color: "#ffffff",
        opacity: props.disabled ? 0.7 : 1,
      }}
      {...props}
    >
      {children}
    </button>
  )
}

export function SignUpPage() {
  const { colors } = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const { error, showError, clearError, isErrorVisible, componentName } = useErrorFeedback()
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmPasswordRef = useRef<HTMLInputElement>(null)
  const { user } = useAuthStore()

  useEffect(() => {
    if (user) {
      navigate("/today", { replace: true })
    }
  }, [user, navigate])

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!emailRef.current?.value || !passwordRef.current?.value) {
      showError(new Error(t("auth.signUpErrors.missingFields")), "SignUpPage.handleSignUp")
      return
    }

    if (passwordRef.current.value !== confirmPasswordRef.current?.value) {
      showError(new Error(t("auth.signUpErrors.passwordMismatch")), "SignUpPage.handleSignUp")
      return
    }

    try {
      setIsLoading(true)
      const { user } = await createUserWithEmailAndPassword(emailRef.current.value, passwordRef.current.value)
      await createUserProfile(user)
      setIsLoading(false)
    } catch (error: unknown) {
      setIsLoading(false)
      let errorMessage = t("auth.signUpErrors.default")
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === "auth/email-already-in-use") {
          errorMessage = t("auth.signUpErrors.emailInUse")
        } else if (error.code === "auth/invalid-email") {
          errorMessage = t("auth.signUpErrors.invalidEmail")
        } else if (error.code === "auth/operation-not-allowed") {
          errorMessage = t("auth.signUpErrors.notAllowed")
        } else if (error.code === "auth/weak-password") {
          errorMessage = t("auth.signUpErrors.weakPassword")
        }
      }
      showError(new Error(errorMessage), "SignUpPage.handleSignUp")
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-md w-full space-y-6 p-8 rounded-lg shadow-lg" style={{ backgroundColor: colors.surface }}>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: colors.primary }}>
            {t("auth.signUpTitle")}
          </h2>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {t("auth.signUpDescription")}
          </p>
        </div>

        <form onSubmit={handleSignUp} className="space-y-4">
          <Input ref={emailRef} label={t("auth.email")} type="email" id="email" disabled={isLoading} />
          <Input ref={passwordRef} label={t("auth.password")} type="password" id="password" disabled={isLoading} />
          <Input
            ref={confirmPasswordRef}
            label={t("auth.confirmPassword")}
            type="password"
            id="confirmPassword"
            disabled={isLoading}
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("auth.creatingAccount") : t("auth.signUp")}
          </Button>
        </form>

        <div className="text-center mt-6">
          <p style={{ color: colors.textSecondary }}>
            {t("auth.haveAccount")}{" "}
            <Link
              to="/login"
              className="font-medium hover:text-secondary transition-colors duration-200"
              style={{ color: colors.primary }}
            >
              {t("auth.signIn")}
            </Link>
          </p>
        </div>

        {error && (
          <ErrorFeedback error={error} componentName={componentName} isOpen={isErrorVisible} onClose={clearError} />
        )}
      </div>
    </div>
  )
}

export default SignUpPage
